import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import GatsbyModal from './gatsby-modal'
import ApiGateway from 'theme/api/api-gateway'
import { useFirebaseService } from 'theme/services/firebase-service'
import withRuntime from 'theme/hoc/withRuntime'
import { useAuthService } from 'theme/services/auth/auth-service'
import { Rating } from 'react-simple-star-rating'
import { CheckCircleIcon } from '@heroicons/react/outline'

const AddFeedbackModal = ({
  openModal,
  handleClose,
  data,
  session,
  conferenceId,
  reloadFeedbackCallback,
}) => {
  // console.log(session, conferenceId)
  const firebase = useFirebaseService()
  const authService = useAuthService()
  const user = authService.user
  const feedbackDbRead = useRef(false)
  const [speakerRating, setSpeakerRating] = useState(null)
  const [contentRating, setContentRating] = useState(null)
  const [feedbackComment, setFeedbackComment] = useState('')
  const [userFeedback, setUserFeedback] = useState(null)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  const speakerRatingChanged = (value) => {
    setSpeakerRating(value)
    setFeedbackSubmitted(true)
    if (userFeedback) {
      userFeedback.speakerRating = value
      setUserFeedback(userFeedback)
    } else setUserFeedback({ speakerRating: value })

    ApiGateway.setSessionFeedback(firebase, user.uid, session.id, {
      ...userFeedback,
      uid: user.uid,
      conference: conferenceId,
      sessionId: session.id,
      speakerRating: value,
    })
  }

  const contentRatingChanged = (value) => {
    setContentRating(value)
    setFeedbackSubmitted(true)
    if (userFeedback) {
      userFeedback.contentRating = value
      setUserFeedback(userFeedback)
    } else setUserFeedback({ contentRating: value })

    ApiGateway.setSessionFeedback(firebase, user.uid, session.id, {
      ...userFeedback,
      uid: user.uid,
      conference: conferenceId,
      sessionId: session.id,
      contentRating: value,
    })
  }
  const feedbackCommentChanged = (event) => {
    const value = event.target.value
    setFeedbackComment(value)
    setFeedbackSubmitted(true)
    if (userFeedback) {
      userFeedback.feedbackComment = value
      setUserFeedback(userFeedback)
    } else setUserFeedback({ feedbackComment: value })

    // console.log(userFeedback)
    ApiGateway.setSessionFeedback(firebase, user.uid, session.id, {
      ...userFeedback,
      uid: user.uid,
      conference: conferenceId,
      sessionId: session.id,
      feedbackComment: value,
    })
  }
  const fetchFeedback = () => {
    try {
      ApiGateway.getSessionFeedback(firebase, user.uid, session.id).then((userFeedbackDb) => {
        if (userFeedbackDb) {
          setUserFeedback(userFeedbackDb)
          if (userFeedbackDb.speakerRating) setSpeakerRating(userFeedbackDb.speakerRating)
          if (userFeedbackDb.contentRating) setContentRating(userFeedbackDb.contentRating)
          if (userFeedbackDb.feedbackComment) setFeedbackComment(userFeedbackDb.feedbackComment)
        }

        feedbackDbRead.current = true
      })
    } catch (e) {
      feedbackDbRead.current = true
      console.error(e)
    }
  }

  useEffect(() => {
    if (!user) {
      return
    }

    fetchFeedback()
  }, [user])

  const getSpeakerNames = (speakers) => {
    return speakers.map((speaker) => {
      const companyName =
        speaker.data.Company && speaker.data.Company[0] && speaker.data.Company[0].data.Company_Name
      const details =
        speaker.data.Person[0].data.Person_First +
        ' ' +
        speaker.data.Person[0].data.Person_Last +
        ', ' +
        speaker.data.Person_Job_Title
      const returnString = companyName ? details + ', ' + companyName : details
      return returnString
    })
  }

  const publishedSpeakers = session.data.Speakers?.filter((speaker) => {
    // Check if both speaker and speaker's Person data are present
    return speaker?.data?.Person && speaker.data.Person[0].data.Person_Status === 'Published'
  })

  return (
    <GatsbyModal openModal={openModal} handleClose={handleClose}>
      <div className="flex-1 flex flex-col justify-center pt-12 pb-16 px-4 sm:px-6 lg:flex-none lg:px-20 lg:pt-10">
        <h2 className="text-2xl">Did you enjoy the session?</h2>
        <div className="">
          <div>
            <p className="text-lg pt-4">{session.data.Talk_Title}</p>
            {getSpeakerNames(publishedSpeakers).map((speaker, index) => {
              return <p key={index}>{speaker}</p>
            })}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
          <div>
            <div className="mt-1">
              <label
                htmlFor="speakerRating"
                className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
              >
                Speaker
              </label>
              <Rating
                id="speakerRating"
                name="speakerRating"
                onClick={speakerRatingChanged}
                size={24}
                ratingValue={speakerRating}
              />
            </div>
            <div className="mt-4">
              <label
                htmlFor="contentRating"
                className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
              >
                Content
              </label>
              <Rating
                id="contentRating"
                name="contentRating"
                onClick={contentRatingChanged}
                size={24}
                ratingValue={contentRating}
              />
            </div>
          </div>
          <div className="mt-1 sm:mt-0 sm:col-span-2">
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
              Comment
            </label>
            <div className="mt-1 sm:mt-0">
              <textarea
                id="comment"
                name="comment"
                rows={3}
                className="max-w-lg shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border border-gray-300 rounded-md"
                value={feedbackComment}
                onChange={feedbackCommentChanged}
              />
              <p className="mt-2 text-sm text-gray-500">
                Some helpful context around your ratings can really guide speakers on where to improve.
              </p>
            </div>
          </div>
          {/* {feedbackSubmitted && (
            <p className="mt-2 text-xs text-green-700 flex">
              <CheckCircleIcon className="w-4 h-4 mr-1" />
              Feedback auto-saved.
            </p>
          )} */}
        </div>
        <div className="block pt-12">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={handleClose}
          >
            Share Feedback
          </button>
        </div>
      </div>
    </GatsbyModal>
  )
}

AddFeedbackModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  session: PropTypes.object.isRequired,
  reloadFeedbackCallback: PropTypes.func.isRequired,
}

export default withRuntime(AddFeedbackModal)
